/**
 * تهيئة هيكل منشأة جديدة في الوضع المحلي.
 *
 * تُستخدم من الإنشاء اليدوي (لوحة المنصة) ومن التسجيل الذاتي، حتى لا تختلف
 * الأدوار أو الفئات أو وحدات التشغيل بين المسارين. لا تنشئ المنشأة نفسها.
 */

import { newId } from '@/domain/ids';
import { COMPANY_PERMISSIONS, DEFAULT_ROLE_BLUEPRINTS } from '@/domain/permissions';
import type { Company, Membership, Role } from '@/domain/types';
import type { LocalCore } from './commands';

export interface CompanySeedOptions {
  company: Company;
  ownerUserId: string;
  now: string;
  /** التسجيل الذاتي ينشئ صاحب منشأة؛ الإنشاء من لوحة المنصة لا يضيف المالك عضواً. */
  createOwnerMembership?: boolean;
}

export async function seedCompanyStructure(
  core: LocalCore,
  options: CompanySeedOptions,
): Promise<Membership | null> {
  const { company, ownerUserId, now } = options;

  const categories = [
    { key: 'work', name: 'عمل', kind: 'work' as const, color: '#1f6f6b', icon: 'briefcase' },
    { key: 'personal', name: 'شخصية', kind: 'personal' as const, color: '#7c6f9c', icon: 'user' },
    { key: 'other', name: 'مهام أخرى', kind: 'other' as const, color: '#c9825a', icon: 'box' },
  ];
  for (const [index, category] of categories.entries()) {
    await core.rawPut('task_categories', {
      id: newId(),
      companyId: company.id,
      key: category.key,
      name: category.name,
      description: null,
      kind: category.kind,
      color: category.color,
      icon: category.icon,
      isSystem: true,
      sortOrder: index,
      status: 'active',
      createdAt: now,
      updatedAt: now,
    });
  }

  let membership: Membership | null = null;
  if (options.createOwnerMembership !== false) {
    membership = {
    id: newId(),
    companyId: company.id,
    userId: ownerUserId,
    employeeNo: null,
    jobTitle: 'مسؤول المنشأة',
    status: 'active',
    isCompanyOwner: true,
    primaryBranchId: null,
    workingDays: [0, 1, 2, 3, 4, 6],
    joinedAt: now,
    endedAt: null,
    createdAt: now,
    updatedAt: now,
    };
    await core.rawPut('memberships', membership);
    const accountProfile = await core.rawGet('profiles', ownerUserId);
    await core.rawPut('membership_profiles', {
      id: membership.id, membershipId: membership.id, userId: ownerUserId, companyId: company.id,
      fullName: accountProfile?.fullName ?? 'مسؤول المنشأة', phone: null, avatarDataUrl: null,
      createdAt: now, updatedAt: now,
    });
  }

  for (const moduleKey of ['core_tasks', 'reports', 'attachments'] as const) {
    await core.rawPut('company_modules', {
      id: newId(),
      companyId: company.id,
      moduleKey,
      enabled: true,
      settings: {},
      enabledAt: now,
      enabledBy: ownerUserId,
    });
  }

  for (const blueprint of DEFAULT_ROLE_BLUEPRINTS) {
    const role: Role = {
      id: newId(),
      companyId: company.id,
      key: blueprint.key,
      name: blueprint.name,
      description: blueprint.description,
      priority: blueprint.priority,
      isSystem: true,
      isPlatformRole: false,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    };
    await core.rawPut('roles', role);
    for (const permission of blueprint.permissions.filter((p) => COMPANY_PERMISSIONS.includes(p))) {
      await core.rawPut('role_permissions', {
        id: newId(),
        roleId: role.id,
        permissionKey: permission,
      });
    }
    const scopeType =
      blueprint.key === 'company_admin' || blueprint.key === 'brands_manager'
        ? 'company'
        : blueprint.key === 'branch_manager' || blueprint.key === 'supervisor'
          ? 'branch'
          : blueprint.key === 'hr_supervisor'
            ? 'supervised'
            : 'self';
    await core.rawPut('role_scopes', {
      id: newId(),
      roleId: role.id,
      scopeType: scopeType as 'company' | 'branch' | 'supervised' | 'self',
      targetId: null,
    });
    if (membership && blueprint.key === 'company_admin') {
      await core.rawPut('membership_roles', {
        id: newId(),
        membershipId: membership.id,
        roleId: role.id,
        assignedBy: ownerUserId,
        assignedAt: now,
        expiresAt: null,
      });
    }
  }

  return membership;
}
